import React, { useState, useEffect } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, TextField, Typography, Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import { MODALITY_OPTIONS } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import UserService from '../../services/user.service';
import WorkFeedbackService from '../../services/work-feedback.service';

const useStyles = makeStyles((theme) => ({
  buttonsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  cancelButton: {
    border: '1px solid #e0e0e0',
    padding: theme.spacing(1.5),
    width: '100%',
  },
  checkboxContainer: {
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    color: theme.palette.grey[500],
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  dialogActions: {
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogPaper: {
    maxWidth: '500px',
    width: '100%',
  },
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  formControl: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  instructionText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#3d8bd1',
    },
    backgroundColor: '#4dabf5',
    color: 'white',
    padding: theme.spacing(1.5),
    width: '100%',
  },
}));

const CreateWorkFeedbackModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { workspaceId } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    examName: '',
    modality: 'xray',
    note: '',
    patientName: '',
    technicianId: '',
  });

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setLoadingUsers(true);
        const userService = new UserService();
        const response = await userService.getUsers({
          limit: 100,
          status: 'Active',
        });

        const sortedUsers = response?.data?.sort((a, b) => a.name.localeCompare(b.name)) || [];

        setUsers(sortedUsers);
      } catch (error) {
        console.log('Error fetching technicians:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchTechnicians();
  }, [workspaceId]);

  const handleSubmit = async () => {
    try {
      if (!formData.technicianId) {
        enqueueSnackbar('Please select a technician', { variant: 'error' });
        return;
      }

      setLoading(true);

      const dataToSubmit = {
        examName: formData.examName,
        modality: formData.modality,
        patientName: formData.patientName,
        technicianId: formData.technicianId,
      };

      await WorkFeedbackService.create(dataToSubmit);

      enqueueSnackbar('Work feedback created successfully! An email notification has been sent to the technician.', { variant: 'success' });
      handleClose();
    } catch (error) {
      console.error('Error creating work feedback:', error);
      enqueueSnackbar('Failed to create work feedback', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      examName: '',
      modality: 'xray',
      note: '',
      patientName: '',
      technicianId: '',
    });

    onClose();
  };

  const selectMenuProps = {
    anchorOrigin: {
      horizontal: 'left',
      vertical: 'bottom',
    },
    getContentAnchorEl: null,
    transformOrigin: {
      horizontal: 'left',
      vertical: 'top',
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        Create Work Feedback
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" className={classes.instructionText}>
          Recognize great work by a technician! This will send them a congratulatory email.
        </Typography>

        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            id="technicianId"
            name="technicianId"
            value={formData.technicianId}
            onChange={handleChange}
            fullWidth
            required
            disabled={loadingUsers}
            displayEmpty
            MenuProps={selectMenuProps}>
            <MenuItem value="" disabled>
              <em>{loadingUsers ? 'Loading technicians...' : 'Select Technician'}</em>
            </MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} ({user.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" className={classes.formControl}>
          <Select id="modality" name="modality" value={formData.modality} onChange={handleChange} required fullWidth MenuProps={selectMenuProps}>
            {MODALITY_OPTIONS.map((modality) => (
              <MenuItem key={modality.value} value={modality.value}>
                {modality.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          fullWidth
          id="patientName"
          placeholder="Patient Name (Optional)"
          name="patientName"
          value={formData.patientName}
          onChange={handleChange}
          className={classes.formControl}
        />

        <TextField
          variant="outlined"
          fullWidth
          id="examName"
          placeholder="Exam Name (Optional)"
          name="examName"
          value={formData.examName}
          onChange={handleChange}
          className={classes.formControl}
        />
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <div className={classes.buttonsContainer}>
          <Button onClick={handleClose} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" className={classes.submitButton} disabled={loading || !formData.technicianId}>
            {loading ? 'Sending...' : 'Send Feedback'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CreateWorkFeedbackModal;
